/**
 * React Query hooks for /api/v1/pressure-testing/* (fieldiq-api#907).
 *
 * EVERY HOOK RETURNS THE META. A PT read is `{ data, meta }`, and a screen
 * cannot label a single pressure without `meta.pressure_unit`, or default a
 * calibration date without `meta.today` (the tenant's day, not the browser's).
 *
 * EVERY KEY STARTS ['pressureTesting'], and every write invalidates that one
 * prefix through `invalidatePressureTesting`. A calibration moves an
 * instrument's status, which moves the register, its counts, the alerts list
 * and the detail; a settings change moves the warn window, which moves the
 * status of every instrument. One prefix is the honest answer to "what did
 * this write change".
 *
 * EVERY HOOK SPREADS LOAD_FAILURE_QUERY_OPTIONS. Read a failure with
 * `queryLoadFailure(query)`, not `query.error` (lib/load-failure.ts).
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { putToPresignedUrl } from '../api/upload';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { compressImage } from '../lib/offline/image-compress';
import { recordServerClockOffset } from '../lib/pt-hold-clock';
import type {
  PtJob,
  PtJobCreate,
  PtJobDetail,
  PtJobPatch,
  PtTest,
  PtTestType,
  PtTestTypeCreate,
  PtTestWrite,
  PtCalibration,
  PtCalibrationCreate,
  PtCertificate,
  PtCertificateListRow,
  PtInstrument,
  PtInstrumentDetail,
  PtInstrumentKind,
  PtMeta,
  PtResult,
  PtSettings,
  PtSettingsUpdate,
  PtSignatures,
  PtSignatureWrite,
  PtSpecsPatch,
  PtConsumable,
  PtCustomerSettings,
  PtLineCreate,
  PtLinePatch,
  PtRate,
  PtRateCreate,
  PtRatePatch,
  PtTicket,
  PtTicketDetail,
  PtTicketMode,
  PtTicketSign,
  PtBillableJob,
  PtInvoice,
  PtInvoiceGenerate,
  PtInvoiceListRow,
  PtInvoiceRecipient,
  PtInvoiceSent,
} from '../types/pressure-testing';

const EMPTY_META: PtMeta = {
  unit_system: null,
  unit_system_source: null,
  pressure_unit: null,
  time_zone: null,
  today: null,
  cal_warn_days: null,
  currency: null,
};

/**
 * Split a PT body into payload and meta. `data: null` stays null — never the
 * envelope (the `res.data?.data || res.data` bug in CLAUDE.md). A missing meta
 * is all-null, so a screen prints `—` and no unit rather than guessing one.
 */
export function unwrapPt<T>(body: unknown): PtResult<T | null> & { warnings: string[] } {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    return { data: null, meta: EMPTY_META, warnings: [] };
  }
  const b = body as { data?: unknown; meta?: Partial<PtMeta>; warnings?: unknown };
  const data = b.data === undefined || b.data === null ? null : (b.data as T);
  const warnings = Array.isArray(b.warnings) ? b.warnings.filter((w): w is string => typeof w === 'string') : [];
  return { data, meta: { ...EMPTY_META, ...(b.meta ?? {}) }, warnings };
}

/** A list read: a non-array payload becomes `[]` rather than reaching `.map`. */
export function unwrapPtList<T>(body: unknown): PtResult<T[]> {
  const { data, meta } = unwrapPt<unknown>(body);
  return { data: Array.isArray(data) ? (data as T[]) : [], meta };
}

export const pressureTestingKeys = {
  all: ['pressureTesting'] as const,
  settings: () => ['pressureTesting', 'settings'] as const,
  instruments: (params?: Record<string, unknown>) => ['pressureTesting', 'instruments', params ?? {}] as const,
  instrument: (id: string) => ['pressureTesting', 'instrument', id] as const,
  alerts: () => ['pressureTesting', 'alerts'] as const,
  testTypes: () => ['pressureTesting', 'testTypes'] as const,
  jobs: (params?: Record<string, unknown>) => ['pressureTesting', 'jobs', params ?? {}] as const,
  job: (id: string) => ['pressureTesting', 'job', id] as const,
  myJobs: () => ['pressureTesting', 'field', 'myJobs'] as const,
  chartUpload: (testId: string, key: string) => ['pressureTesting', 'chartUpload', testId, key] as const,
  certificates: (params?: Record<string, unknown>) => ['pressureTesting', 'certificates', params ?? {}] as const,
  certificate: (id: string) => ['pressureTesting', 'certificate', id] as const,
  rates: (params?: Record<string, unknown>) => ['pressureTesting', 'rates', params ?? {}] as const,
  consumables: (params?: Record<string, unknown>) => ['pressureTesting', 'consumables', params ?? {}] as const,
  customerSettings: (customerId: string) => ['pressureTesting', 'customerSettings', customerId] as const,
  customerSettingsList: () => ['pressureTesting', 'customerSettings', 'list'] as const,
  tickets: (params?: Record<string, unknown>) => ['pressureTesting', 'tickets', params ?? {}] as const,
  ticket: (id: string) => ['pressureTesting', 'ticket', id] as const,
  jobTickets: (jobId: string) => ['pressureTesting', 'jobTickets', jobId] as const,
  billable: () => ['pressureTesting', 'invoices', 'billable'] as const,
  invoices: (params?: Record<string, unknown>) => ['pressureTesting', 'invoices', 'list', params ?? {}] as const,
  invoice: (id: string) => ['pressureTesting', 'invoice', id] as const,
  invoiceRecipient: (id: string) => ['pressureTesting', 'invoice', id, 'recipient'] as const,
  ticketInvoice: (ticketId: string) => ['pressureTesting', 'ticketInvoice', ticketId] as const,
};

/**
 * The one invalidation every PT write uses: instruments, calibrations, alerts,
 * settings, and (bundle 2) test types, jobs and tests. A test write moves its
 * job's tallies on the list; a status change moves the stats; one prefix.
 *
 * PT-4 adds certificates under the same prefix, and it has to be the same
 * one: completing a job drafts its certificate, a signature or a test edit
 * re-runs the certificate's flags, and an issue moves the job card and the
 * Test Jobs "awaiting issue" count. Every one of those crosses jobs and
 * certificates, so neither gets an invalidation of its own.
 *
 * PT-5 adds rates, customer modes and field tickets under it too, for the same
 * reason: completing a job drafts its ticket, a rate change moves a reprice, a
 * void frees the job for a replacement, and a ticket's write moves the list,
 * the job's Tickets card and the field review at once.
 *
 * PT-6 adds invoices under it as well: generating one moves its tickets
 * approved → invoiced (the ticket list, drawer, job card and Ready to invoice
 * all change) and a void moves them back. Invoices, tickets and jobs are one
 * write's consequences, so they share this one invalidation.
 */
export function invalidatePressureTesting(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: pressureTestingKeys.all });
}

export function usePtSettings() {
  return useQuery({
    queryKey: pressureTestingKeys.settings(),
    queryFn: async () => unwrapPt<PtSettings>((await api.pressureTesting.settings.get()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePtInstruments(params?: { status?: string; kind?: string; q?: string }) {
  return useQuery({
    queryKey: pressureTestingKeys.instruments(params),
    queryFn: async () => unwrapPtList<PtInstrument>((await api.pressureTesting.instruments.list(params)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePtInstrument(id: string | undefined) {
  return useQuery({
    queryKey: pressureTestingKeys.instrument(id ?? ''),
    queryFn: async () => unwrapPt<PtInstrumentDetail>((await api.pressureTesting.instruments.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePtCalibrationAlerts() {
  return useQuery({
    queryKey: pressureTestingKeys.alerts(),
    queryFn: async () => unwrapPtList<PtInstrument>((await api.pressureTesting.calibrationAlerts()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

function usePtWrite<V, R>(call: (vars: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: V) => unwrapPt<R>((await call(vars)).data),
    onSuccess: () => invalidatePressureTesting(qc),
  });
}

export function useUpdatePtSettings() {
  return usePtWrite<PtSettingsUpdate, PtSettings>((body) => api.pressureTesting.settings.update(body));
}

export function useUpdatePtSpecs() {
  return usePtWrite<{ id: string; body: PtSpecsPatch }, PtInstrument>(({ id, body }) =>
    api.pressureTesting.instruments.updateSpecs(id, body),
  );
}

export function useRecordPtCalibration() {
  return usePtWrite<{ id: string; body: PtCalibrationCreate }, PtCalibration>(({ id, body }) =>
    api.pressureTesting.instruments.recordCalibration(id, body),
  );
}

export interface PtNewInstrument {
  /** Set on a retry after step one succeeded: reuse that row instead of making a second. */
  equipmentId?: string | null;
  equipment: {
    name: string;
    type: string;
    serialNumber?: string | null;
    manufacturer?: string | null;
    model?: string | null;
  };
  specs: {
    instrument_kind: PtInstrumentKind;
    range_min?: number | null;
    range_max?: number | null;
    accuracy_class?: string | null;
    recorder_channels?: number | null;
  };
}

/**
 * Step two failed after step one succeeded. The equipment row EXISTS; the
 * caller keeps `equipmentId` and retries step two with it, rather than making
 * a duplicate unit (which the serial dedupe would refuse with a 409 anyway).
 */
export class PtSpecsAttachError extends Error {
  constructor(public readonly equipmentId: string, public readonly attachError: unknown) {
    super('The equipment row was created, but its instrument specs were not saved.');
    this.name = 'PtSpecsAttachError';
  }
}

/**
 * Add instrument — two calls, because that is the API's contract: the shared
 * equipment create (serial dedupe, status and yard rules) is not copied into
 * the PT routes. POST /equipment, then POST /pressure-testing/instruments with
 * the new row's id. Equipment create bodies are camelCase (`serialNumber`).
 */
export function useCreatePtInstrument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: PtNewInstrument) => {
      let equipmentId = vars.equipmentId ?? null;
      if (!equipmentId) {
        const res = await api.equipment.create({ ...vars.equipment, status: 'available' });
        const body = res.data as { data?: { id?: unknown } } | null;
        const id = body && typeof body === 'object' && 'data' in body ? body.data?.id : undefined;
        if (typeof id !== 'string' || !id) throw new Error('The equipment create answered without an id.');
        equipmentId = id;
        qc.invalidateQueries({ queryKey: ['equipment'] });
      }
      try {
        const res = await api.pressureTesting.instruments.create({ equipment_id: equipmentId, ...vars.specs });
        return unwrapPt<PtInstrument>(res.data);
      } catch (e) {
        throw new PtSpecsAttachError(equipmentId, e);
      }
    },
    onSuccess: () => invalidatePressureTesting(qc),
  });
}

// ─── Bundle 2 (fieldiq-api#915): test types, jobs, tests ─────────────────────

export function usePtTestTypes() {
  return useQuery({
    queryKey: pressureTestingKeys.testTypes(),
    queryFn: async () => unwrapPtList<PtTestType>((await api.pressureTesting.testTypes.list()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePtJobs(params?: { status?: string; q?: string }) {
  return useQuery({
    queryKey: pressureTestingKeys.jobs(params),
    queryFn: async () => unwrapPtList<PtJob>((await api.pressureTesting.jobs.list(params)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePtJob(id: string | undefined) {
  return useQuery({
    queryKey: pressureTestingKeys.job(id ?? ''),
    queryFn: async () => unwrapPt<PtJobDetail>((await api.pressureTesting.jobs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useCreatePtTestType() {
  return usePtWrite<PtTestTypeCreate, PtTestType>((body) => api.pressureTesting.testTypes.create(body));
}

export function useCreatePtJob() {
  return usePtWrite<PtJobCreate, PtJob>((body) => api.pressureTesting.jobs.create(body));
}

/** Fields or a status change (Start / Complete / Cancel). */
export function useUpdatePtJob() {
  return usePtWrite<{ id: string; body: PtJobPatch }, PtJob>(({ id, body }) => api.pressureTesting.jobs.update(id, body));
}

export function useCreatePtTest() {
  return usePtWrite<{ jobId: string; body: PtTestWrite }, PtTest>(({ jobId, body }) =>
    api.pressureTesting.tests.create(jobId, body),
  );
}

export function useUpdatePtTest() {
  return usePtWrite<{ id: string; body: PtTestWrite }, PtTest>(({ id, body }) => api.pressureTesting.tests.update(id, body));
}

// ─── PT-3 (fieldiq-api#917): the field flow ──────────────────────────────────

/** GET /field/my-jobs — the API's own selection and order (active, scheduled, recently complete). */
export function useMyPtJobs() {
  return useQuery({
    queryKey: pressureTestingKeys.myJobs(),
    queryFn: async () => unwrapPtList<PtJob>((await api.pressureTesting.field.myJobs()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Start or stop the hold. The server stamps the instant; this sends the start
 * pressure only. Each response's `server_now` re-measures the device clock's
 * offset (lib/pt-hold-clock.ts) so the countdown runs on the server's time.
 */
function usePtHoldWrite<V>(call: (vars: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: V) => {
      const sentAt = Date.now();
      const res = await call(vars);
      const receivedAt = Date.now();
      const body = res.data as { server_now?: unknown } | null;
      if (body && typeof body === 'object') recordServerClockOffset(body.server_now, sentAt, receivedAt);
      return unwrapPt<PtTest>(res.data);
    },
    onSuccess: () => invalidatePressureTesting(qc),
  });
}

export function useStartPtHold() {
  return usePtHoldWrite<{ id: string; startPressure: number | null }>(({ id, startPressure }) =>
    // Absent when the test already carries a start pressure: the API uses the stored one.
    api.pressureTesting.tests.holdStart(id, startPressure === null ? {} : { start_pressure: startPressure }),
  );
}

export function useStopPtHold() {
  return usePtHoldWrite<{ id: string }>(({ id }) => api.pressureTesting.tests.holdStop(id));
}

export function useCompletePtJob() {
  return usePtWrite<{ id: string }, PtJob>(({ id }) => api.pressureTesting.jobs.complete(id));
}

/** The chart MIME types the API accepts (pressure-testing-jobs.routes.ts CHART_MIME_TYPES). */
const CHART_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif']);

/**
 * The chart photo, in four steps that are the API's contract: presign with
 * entityType 'pt_test_chart' and entityId = the test, PUT to R2, confirm, then
 * claim the key onto the test. ONLINE ONLY, deliberately: the offline upload
 * queue (lib/offline/upload-sync.ts) does steps 1–3 and has no step 4, and the
 * key does not exist until the flush — so a queued chart would land in R2 and
 * never reach the test, and Pass would keep being refused with no visible
 * reason. The screen refuses to start offline instead.
 *
 * Every step throws on failure, so the caller shows one message and nothing
 * reads as attached unless the claim answered 200.
 */
export function useAttachPtChart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ testId, file }: { testId: string; file: File }) => {
      let body: Blob = file;
      let contentType = file.type || 'image/jpeg';
      let fileName = file.name || 'chart.jpg';
      // Shrink a camera JPEG/PNG for one bar of signal. A HEIC the browser
      // cannot decode is sent as taken; the API accepts it.
      if (contentType !== 'image/heic' && contentType !== 'image/heif') {
        try {
          body = await compressImage(file);
          contentType = 'image/jpeg';
          fileName = fileName.replace(/\.[^.]+$/, '') + '.jpg';
        } catch {
          body = file;
        }
      }
      if (!CHART_TYPES.has(contentType.toLowerCase())) {
        throw new Error('A chart photo must be a JPEG, PNG or HEIC image.');
      }
      const presign = await api.uploads.getPresignedUrl({ fileName, contentType, entityType: 'pt_test_chart', entityId: testId });
      const p = presign.data as { uploadUrl?: unknown; key?: unknown };
      if (typeof p?.uploadUrl !== 'string' || typeof p?.key !== 'string') {
        throw new Error('The upload could not be prepared. Try again.');
      }
      // Throws unless storage answered 2xx — nothing is confirmed that is not in the bucket.
      await putToPresignedUrl(p.uploadUrl, body, contentType);
      await api.uploads.confirm({ key: p.key, size: body.size });
      return unwrapPt<PtTest>((await api.pressureTesting.tests.attachChart(testId, p.key)).data);
    },
    onSuccess: () => invalidatePressureTesting(qc),
  });
}

/**
 * The uploads row behind a test's chart, from GET /uploads/pt_test_chart/:testId,
 * matched on `s3Key` = `chart.file_key`. Its `id` is what the authenticated
 * download door takes. Null when the list holds no such row.
 */
export function usePtChartUpload(testId: string | undefined, fileKey: string | null | undefined) {
  return useQuery({
    queryKey: pressureTestingKeys.chartUpload(testId ?? '', fileKey ?? ''),
    queryFn: async () => {
      const res = await api.uploads.listForEntity('pt_test_chart', testId as string);
      const body = res.data as { data?: unknown } | null;
      const rows = body && typeof body === 'object' && 'data' in body && Array.isArray(body.data) ? (body.data as Record<string, unknown>[]) : [];
      const row = rows.find((r) => (r.s3Key ?? r.key) === fileKey);
      return row && typeof row.id === 'string' ? { id: row.id } : null;
    },
    enabled: !!testId && !!fileKey,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── PT-4 (fieldiq-api#921): certificates and signatures ─────────────────────

export type PtCertificateListParams = { status?: string; q?: string };

/** GET /certificates. Unpaginated: the API answers every certificate the caller may read. */
export function usePtCertificates(params?: PtCertificateListParams) {
  return useQuery({
    queryKey: pressureTestingKeys.certificates(params),
    queryFn: async () => unwrapPtList<PtCertificateListRow>((await api.pressureTesting.certificates.list(params)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePtCertificate(id: string | null | undefined) {
  return useQuery({
    queryKey: pressureTestingKeys.certificate(id ?? ''),
    queryFn: async () => unwrapPt<PtCertificate>((await api.pressureTesting.certificates.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Build the draft for a complete job that has none, or rebuild a draft / rejected one. */
export function useDraftPtCertificate() {
  return usePtWrite<{ jobId: string }, PtCertificate>(({ jobId }) => api.pressureTesting.certificates.draft(jobId));
}

export function useIssuePtCertificate() {
  return usePtWrite<{ id: string; email: boolean }, PtCertificate>(({ id, email }) =>
    api.pressureTesting.certificates.issue(id, { email }),
  );
}

export function useRejectPtCertificate() {
  return usePtWrite<{ id: string; reason: string }, PtCertificate>(({ id, reason }) =>
    api.pressureTesting.certificates.reject(id, reason),
  );
}

/** The two signatures as the API answers them after the write. */
export function useSignPtJob() {
  return usePtWrite<{ jobId: string; body: PtSignatureWrite }, PtSignatures>(({ jobId, body }) =>
    api.pressureTesting.signatures.create(jobId, body),
  );
}

// ─── PT-5 (fieldiq-api#932): rates, customer ticket mode, field tickets ──────

export type PtRateListParams = { kind?: string; customer_id?: string; include_expired?: 'true' };

/** GET /rates (PT_ADMIN). Unpaginated. */
export function usePtRates(params?: PtRateListParams, enabled = true) {
  return useQuery({
    queryKey: pressureTestingKeys.rates(params),
    queryFn: async () => unwrapPtList<PtRate>((await api.pressureTesting.rates.list(params)).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useCreatePtRate() {
  return usePtWrite<PtRateCreate, PtRate>((body) => api.pressureTesting.rates.create(body));
}

export function useUpdatePtRate() {
  return usePtWrite<{ id: string; body: PtRatePatch }, PtRate>(({ id, body }) => api.pressureTesting.rates.update(id, body));
}

export function useDeletePtRate() {
  return usePtWrite<{ id: string }, { id: string; deleted: boolean }>(({ id }) => api.pressureTesting.rates.remove(id));
}

/** The consumables a line may name, for the job's customer on a day. */
export function usePtConsumables(params: { customer_id?: string; on?: string }, enabled = true) {
  return useQuery({
    queryKey: pressureTestingKeys.consumables(params),
    queryFn: async () => unwrapPtList<PtConsumable>((await api.pressureTesting.consumables(params)).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePtCustomerSettings(customerId: string | null | undefined) {
  return useQuery({
    queryKey: pressureTestingKeys.customerSettings(customerId ?? ''),
    queryFn: async () => unwrapPt<PtCustomerSettings>((await api.pressureTesting.customerSettings.get(customerId as string)).data),
    enabled: !!customerId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Every customer's mode in one read (PT-6, api#939 `GET /customers/settings`). */
export function usePtCustomerSettingsList() {
  return useQuery({
    queryKey: pressureTestingKeys.customerSettingsList(),
    queryFn: async () => unwrapPtList<PtCustomerSettings>((await api.pressureTesting.customerSettings.list()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useUpdatePtCustomerSettings() {
  return usePtWrite<{ customerId: string; ticketMode: PtTicketMode | null }, PtCustomerSettings>(({ customerId, ticketMode }) =>
    api.pressureTesting.customerSettings.update(customerId, ticketMode),
  );
}

export type PtTicketListParams = { status?: string; customer_id?: string; job_id?: string; from?: string; to?: string };

/** GET /field-tickets. Unpaginated; a field worker gets only their own jobs' tickets. */
export function usePtTickets(params?: PtTicketListParams) {
  return useQuery({
    queryKey: pressureTestingKeys.tickets(params),
    queryFn: async () => unwrapPtList<PtTicket>((await api.pressureTesting.tickets.list(params)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePtTicket(id: string | null | undefined) {
  return useQuery({
    queryKey: pressureTestingKeys.ticket(id ?? ''),
    queryFn: async () => unwrapPt<PtTicketDetail>((await api.pressureTesting.tickets.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** GET /jobs/:id/tickets — the job's tickets in work-day order. */
export function usePtJobTickets(jobId: string | null | undefined) {
  return useQuery({
    queryKey: pressureTestingKeys.jobTickets(jobId ?? ''),
    queryFn: async () => unwrapPtList<PtTicket>((await api.pressureTesting.tickets.forJob(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useCreatePtTicket() {
  return usePtWrite<{ jobId: string }, PtTicketDetail>(({ jobId }) => api.pressureTesting.tickets.create(jobId));
}

export function useClosePtDay() {
  return usePtWrite<{ jobId: string; workDate: string }, PtTicketDetail>(({ jobId, workDate }) =>
    api.pressureTesting.tickets.closeDay(jobId, workDate),
  );
}

export function useAddPtLine() {
  return usePtWrite<{ ticketId: string; body: PtLineCreate }, PtTicketDetail>(({ ticketId, body }) =>
    api.pressureTesting.tickets.addLine(ticketId, body),
  );
}

export function useUpdatePtLine() {
  return usePtWrite<{ lineId: string; body: PtLinePatch }, PtTicketDetail>(({ lineId, body }) =>
    api.pressureTesting.tickets.updateLine(lineId, body),
  );
}

export function useRemovePtLine() {
  return usePtWrite<{ lineId: string; reason: string | null }, PtTicketDetail>(({ lineId, reason }) =>
    api.pressureTesting.tickets.removeLine(lineId, reason),
  );
}

export function useRepricePtTicket() {
  return usePtWrite<{ ticketId: string }, PtTicketDetail>(({ ticketId }) => api.pressureTesting.tickets.reprice(ticketId));
}

export function useSignPtTicket() {
  return usePtWrite<{ ticketId: string; body: PtTicketSign }, PtTicketDetail>(({ ticketId, body }) =>
    api.pressureTesting.tickets.sign(ticketId, body),
  );
}

export function useApprovePtTicket() {
  return usePtWrite<{ ticketId: string }, PtTicketDetail>(({ ticketId }) => api.pressureTesting.tickets.approve(ticketId));
}

export function useVoidPtTicket() {
  return usePtWrite<{ ticketId: string; reason: string }, PtTicketDetail>(({ ticketId, reason }) =>
    api.pressureTesting.tickets.void(ticketId, reason),
  );
}

// ─── PT-6 (fieldiq-api#939): invoices ────────────────────────────────────────

/** GET /invoices/billable — approved tickets, grouped by job (customer on each). */
export function usePtBillable(enabled = true) {
  return useQuery({
    queryKey: pressureTestingKeys.billable(),
    queryFn: async () => unwrapPtList<PtBillableJob>((await api.pressureTesting.invoices.billable()).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The cap the unified register allows per page. */
export const PT_INVOICE_PAGE_LIMIT = 100;

/**
 * The PT invoice list: the unified register's PT branch, newest first, one
 * page of 100. `total` is the API's count, so a screen can say "showing 100 of
 * N" rather than present one page as the whole list.
 */
export function usePtInvoices(params?: { status?: string; customerId?: string }, enabled = true) {
  return useQuery({
    queryKey: pressureTestingKeys.invoices(params),
    queryFn: async () => {
      const body = (await api.pressureTesting.invoices.list({ ...params, limit: PT_INVOICE_PAGE_LIMIT })).data as unknown;
      const b = body && typeof body === 'object' ? (body as { data?: unknown; pagination?: { total?: unknown } }) : {};
      const rows = Array.isArray(b.data) ? (b.data as PtInvoiceListRow[]) : [];
      const total = typeof b.pagination?.total === 'number' ? b.pagination.total : null;
      return { rows, total };
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePtInvoice(id: string | null | undefined) {
  return useQuery({
    queryKey: pressureTestingKeys.invoice(id ?? ''),
    queryFn: async () => unwrapPt<PtInvoice>((await api.pressureTesting.invoices.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePtInvoiceRecipient(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: pressureTestingKeys.invoiceRecipient(id ?? ''),
    queryFn: async () => unwrapPt<PtInvoiceRecipient>((await api.pressureTesting.invoices.recipient(id as string)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Which live invoice bills an INVOICED ticket. The ticket read carries no
 * invoice link (api#939 did not add one), so this asks the customer's PT
 * invoices — not void, and on the ticket's job or a multi-job invoice
 * (`source_id` null) — and reads each one's tickets until it finds this one.
 * Usually one request after the list. `null` when nothing matched: the caller
 * says the invoice could not be found rather than naming one.
 *
 * TEMPORARY: remove once the API returns the invoice on the ticket read.
 */
export function usePtTicketInvoice(ticket: { id: string; job_id: string; customer_id: string; status: string } | null) {
  return useQuery({
    queryKey: pressureTestingKeys.ticketInvoice(ticket?.id ?? ''),
    queryFn: async () => {
      const t = ticket as NonNullable<typeof ticket>;
      const listBody = (await api.pressureTesting.invoices.list({ customerId: t.customer_id, limit: PT_INVOICE_PAGE_LIMIT })).data as unknown;
      const rows = listBody && typeof listBody === 'object' && Array.isArray((listBody as { data?: unknown }).data)
        ? ((listBody as { data: PtInvoiceListRow[] }).data)
        : [];
      const candidates = rows.filter((r) => r.status !== 'void' && (r.source_id === t.job_id || r.source_id === null));
      for (const c of candidates) {
        const inv = unwrapPt<PtInvoice>((await api.pressureTesting.invoices.get(c.id)).data).data;
        if (inv && inv.status !== 'void' && inv.tickets.some((x) => x.id === t.id)) {
          return { id: inv.id, invoice_number: inv.invoice_number };
        }
      }
      return null;
    },
    enabled: !!ticket && ticket.status === 'invoiced',
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useGeneratePtInvoice() {
  return usePtWrite<PtInvoiceGenerate, PtInvoice>((body) => api.pressureTesting.invoices.generate(body));
}

export function useSendPtInvoice() {
  return usePtWrite<{ id: string; to?: string; cc?: string[] }, PtInvoiceSent>(({ id, to, cc }) =>
    api.pressureTesting.invoices.send(id, { ...(to ? { to } : {}), ...(cc && cc.length ? { cc } : {}) }),
  );
}

export function useVoidPtInvoice() {
  return usePtWrite<{ id: string; reason: string }, PtInvoice & { released_ticket_ids: string[] }>(({ id, reason }) =>
    api.pressureTesting.invoices.void(id, reason),
  );
}
